import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Spin, Button as AntdButton, Tooltip, Checkbox } from 'antd';
import { FileExcelOutlined, SendOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { path } from 'ramda';
import moment from 'moment';

import { Constants } from 'core';
import Button from 'components/molecules/button';
import Input from 'components/organisms/input';
import InputWeekdays from 'components/organisms/input-weekdays';
import { useIntl } from 'common/config/i18n';
import { StationsSelectors, SendCommunicationActions, UserInfoSelectors } from 'common/config/store';
import { StyledBlueLink } from 'common/styling/mixins';
import { isFrance } from 'common/config/env';
import { GetCirculationFilters } from 'types';
import { openBlankWindow } from 'common/utils/window';
import FormError from 'components/molecules/form-error';
import FiltersCard from 'components/organisms/filters-card';
import InputSelect from 'components/organisms/select';
import DatePickerForMomentDates from 'components/organisms/datepicker';

const { Text } = Typography;

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

interface Props {
  title?: string;
  fetchAction: (params: GetCirculationFilters) => void;
  refreshAction: (params: GetCirculationFilters) => void;
  exportListAction: (params: GetCirculationFilters) => void;
  pageNumber?: number;
  maxResultPage?: number;
  status?: 'loading' | 'error' | 'success';
  searchDate?: string;
}
interface FormValues {
  serviceName?: string;
  departureStation?: string;
  arrivalStation?: string;
  startDate?: string;
  endDate?: string;
  weekdays?: string;
  includedCancel?: boolean;
  includedRolling?: boolean;
  serviceTypeIDs?: number[];
}
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_MAX_RESULT_PAGE = 10;

const CirculationFilter = (props: FormValues & Props) => {
  const { t } = useIntl();
  const [initializeCommunicationsError, setInitializeCommunicationsError] = useState(false);

  const dispatch = useDispatch();
  const status = useSelector(StationsSelectors.statusSelector);
  const originStations = useSelector(StationsSelectors.originStationsSelector);
  const canWriteCommunications = useSelector(UserInfoSelectors.hasWriteAccess('Communication'));
  const actifUserServiceTypes = useSelector(UserInfoSelectors.getActifUserServiceTypesSelector);

  const initialValues = {
    serviceName: props.serviceName || '',
    departureStation: props.departureStation || '',
    arrivalStation: props.arrivalStation || '',
    startDate: props.startDate || moment().format(),
    endDate: props.endDate || moment().format(),
    weekdays: props.weekdays || '1234567',
    includedCancel: props.includedCancel || true,
    includedRolling: props.includedRolling || false,
    serviceTypeIDs: props.serviceTypeIDs || undefined,
  };
  const formik = useFormik({
    initialValues,
    onSubmit: (values) => props.fetchAction(values),
  });

  const onReset = () => {
    formik.setFieldValue('serviceName', undefined);
    formik.setFieldValue('departureStation', undefined);
    formik.setFieldValue('arrivalStation', undefined);
    formik.setFieldValue('startDate', moment().format());
    formik.setFieldValue('endDate', moment().format());
    formik.setFieldValue('weekdays', '1234567');
    formik.setFieldValue('includedCancel', true);
    formik.setFieldValue('includedRolling', false);
    formik.setFieldValue('serviceTypeIDs', undefined);

    props.fetchAction({ startDate: moment().format(), endDate: moment().format(), includedCancel: true });
  };

  const initializeCommunication = (values: FormValues, situationCategory: string) => {
    setInitializeCommunicationsError(false);
    dispatch(
      SendCommunicationActions.fetchInitializeCommunication(
        {
          ...values,
          startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD') : '',
          endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD') : '',
          IsBulkCommunication: true,
          pageNumber: DEFAULT_PAGE_NUMBER,
          maxResultPage: DEFAULT_MAX_RESULT_PAGE,
        },
        (result) => {
          if (!result.cancelled && !result.error) {
            const { data } = result;
            if (data.isValid) {
              openBlankWindow(`/send-communication/${situationCategory}`);
              dispatch(SendCommunicationActions.setIsBulkCommunication(true));
            } else {
              setInitializeCommunicationsError(true);
            }
          }
        }
      )
    );
  };

  const displayDate = (dateStr: string) =>
    `${t('filtre', 'REFRESH_LABEL')} ${`${t('filtre', 'REFRESH_LABEL_DAY')} ${moment(dateStr).format(
      'DD/MM/YYYY'
    )}`} ${t('filtre', 'REFRESH_LABEL_TIME')} ${moment(dateStr).format('HH:mm:ss')}`;
  const dateToDisplay: string = props.searchDate ? displayDate(props.searchDate) : '';

  const inputSelectStationsData = [
    {
      id: 'stations',
      list: originStations?.map((station) => ({ id: station.uicStationCode, name: station.name })) || [],
    },
  ];

  const inputSelectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        actifUserServiceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('filtre', 'SEARCH_FILTER_TITLE')}
        firstPart={
          <>
            <Input
              width="150px"
              upLabel={t('filtre', 'SEARCH_FILTER_TRAIN_NUMBER')}
              name="serviceName"
              onChange={formik.handleChange}
              value={formik.values.serviceName}
            />
            {!!isFrance && (
              <Tooltip title={t('filtre', 'INCLUDED_ROLLING_TOOLTIP')} placement="right">
                <CheckboxWrapper>
                  <UpLabel>{t('filtre', 'INCLUDED_ROLLING')}</UpLabel>
                  <Checkbox
                    onChange={(e) => formik.setFieldValue('includedRolling', e.target.checked)}
                    checked={formik.values.includedRolling}
                  />
                </CheckboxWrapper>
              </Tooltip>
            )}

            <>
              <div>
                <UpLabel>{t('filtre', 'SEARCH_FILTER_DEPARTURE_STATION')}</UpLabel>
                <InputSelect
                  onChange={(value) => formik.setFieldValue('departureStation', value)}
                  value={formik.values.departureStation}
                  data={inputSelectStationsData}
                  style={{ minWidth: '150px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  popupMatchSelectWidth
                  showSearch
                  allowClear
                />
              </div>
              <div>
                <UpLabel>{t('filtre', 'SEARCH_FILTER_ARRIVAL_STATION')}</UpLabel>
                <InputSelect
                  onChange={(value) => formik.setFieldValue('arrivalStation', value)}
                  value={formik.values.arrivalStation}
                  data={inputSelectStationsData}
                  style={{ minWidth: '150px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  popupMatchSelectWidth
                  showSearch
                  allowClear
                />
              </div>
            </>
            <div>
              <UpLabel>{t('filtre', 'SEARCH_FILTER_DEPARTURE_DATE')}</UpLabel>
              <DatePickerForMomentDates
                allowClear={false}
                value={moment(formik.values.startDate)}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  formik.setFieldValue('startDate', date);
                  if (moment(date).isAfter(moment(formik.values.endDate))) formik.setFieldValue('endDate', date);
                }}
              />
            </div>
            <div>
              <UpLabel>{t('filtre', 'SEARCH_FILTER_END_DATE')}</UpLabel>
              <DatePickerForMomentDates
                defaultValue={moment(formik.values.startDate)}
                allowClear={false}
                disabledDate={(current) => {
                  return current && current < moment(formik.values.startDate);
                }}
                value={moment(formik.values.endDate)}
                style={{ width: '150px', borderRadius: '5px' }}
                format="DD/MM/YYYY"
                onChange={(date) => formik.setFieldValue('endDate', date)}
              />
            </div>
            <div>
              <UpLabel>{t('filtre', 'SEARCH_FILTER_WEEKDAYS')}</UpLabel>
              <InputWeekdays
                value={formik.values.weekdays}
                onChange={(value) => formik.setFieldValue('weekdays', value)}
              />
            </div>
            <CheckboxWrapper>
              <UpLabel>{t('filtre', 'INCLUDED_CANCEL')}</UpLabel>
              <Checkbox
                onChange={(e) => formik.setFieldValue('includedCancel', e.target.checked)}
                checked={formik.values.includedCancel}
              />
            </CheckboxWrapper>
            {actifUserServiceTypes.length > 1 && (
              <div>
                <UpLabel>{t('filtre', 'HISTO_TYPE_CIRCULATION')}</UpLabel>
                <InputSelect
                  onChange={(ids) => {
                    formik.setFieldValue('serviceTypeIDs', ids);
                  }}
                  value={formik.values.serviceTypeIDs}
                  data={inputSelectServiceTypesData}
                  style={{ minWidth: '200px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  showSearch
                  valueAsArray
                />
              </div>
            )}
          </>
        }
        secondPart={
          <>
            <Button
              loading={status === 'loading'}
              fontSize="1.2rem"
              borderRadius="20px"
              htmlType="submit"
              width="200px"
            >
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={onReset}>{t('filtre', 'SEARCH_FILTER_RESET_BUTTON')}</StyledBlueLink>
          </>
        }
        thirdPart={
          <>
            <p className="searchDate">
              {dateToDisplay && <Text>{dateToDisplay}</Text>}
              <Tooltip title={t('filtre', 'SEARCH_FILTER_REFRESH_BUTTON_TOOLTIP')} placement="right">
                <StyledBlueLink
                  onClick={() => props.refreshAction(formik.values)}
                  className="refreshLink"
                  style={{ display: 'inline', margin: 0, fontSize: 'inherit', marginLeft: 10 }}
                >
                  {t('filtre', 'SEARCH_FILTER_REFRESH_BUTTON')}
                </StyledBlueLink>
              </Tooltip>
            </p>
            {props.status === 'loading' && <Spin />}
          </>
        }
        rightPart={
          <>
            <>
              <UpLabel>{t('CirculationPage', 'BULK_COMMUNICATIONS')}</UpLabel>
              <Tooltip
                title={!canWriteCommunications && t('sendCommunication', 'differedCommunication', 'missingAuth')}
                placement="left"
              >
                <AntdButton
                  onClick={() => {
                    initializeCommunication(formik.values, Constants.SITUATION_CATEGORY.PRE_OP);
                  }}
                  disabled={!canWriteCommunications}
                  size="large"
                  icon={
                    <SendOutlined
                      rotate={315}
                      style={{
                        fontSize: '24px',
                        color: `${path(['theme', 'colors', 'grey', 'darker'])}`,
                      }}
                    />
                  }
                >
                  {t('CirculationPage', 'PRE_OP')}
                </AntdButton>
              </Tooltip>
              <Tooltip
                title={!canWriteCommunications && t('sendCommunication', 'differedCommunication', 'missingAuth')}
                placement="left"
              >
                <AntdButton
                  onClick={() => {
                    initializeCommunication(formik.values, Constants.SITUATION_CATEGORY.OP);
                  }}
                  disabled={!canWriteCommunications}
                  size="large"
                  icon={
                    <SendOutlined
                      rotate={315}
                      style={{
                        fontSize: '24px',
                        color: `${path(['theme', 'colors', 'grey', 'darker'])}`,
                      }}
                    />
                  }
                >
                  {t('CirculationPage', 'OP')}
                </AntdButton>
              </Tooltip>
              {initializeCommunicationsError && (
                <FormError message={t('CirculationPage', 'BULK_COMMUNICATIONS_ERROR')} centered />
              )}
            </>
          </>
        }
        topRightPart={
          <FileExcelOutlined
            style={{ fontSize: 40, color: 'rgb(35,127,78)' }}
            onClick={() => props.exportListAction(formik.values)}
          />
        }
      />
    </form>
  );
};
export default CirculationFilter;
